import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import type { RootState } from '../index';
import type { SimulationResultInterface } from 'store/simulation/types';
import { SIMULATION_RESULT } from 'store/simulation/types';
import { UnitTypes } from 'unitData/types';
import type {
  ChamberAnalysisResultInterface,
  FlowInterface,
  GoJSInterface,
  Group,
  PanelMapInterface,
  PortInterface,
  PortsMapInterface,
  UnitInterface,
} from './types';
import {
  ADD_STORAGE_TO_SCREEN_OUTPUTS,
  ADD_UNIT,
  DisplayGraphMode,
  EDIT_FLOW_PROPERTIES,
  EDIT_LAYOUT,
  EDIT_LAYOUTS,
  EDIT_MULTIPLE_UNIT_LOCATIONS,
  EDIT_PORT,
  EDIT_UNIT_FLOW,
  EDIT_UNIT_INFO,
  HIGHLIGHT_NODES,
  INSERT_ON_CONNECTION,
  PanelType,
  PortMode,
  REMOVE_UNIT,
  REPLACE_UNIT,
  TOGGLE_MIRRORING,
} from './types';
import { PSD_CALC_MODE } from 'containers/FlowProperties/types';
import { preDefinedFlowSizes } from 'util/outputFlowCalculations';
import { v4 as uuidv4 } from 'uuid';
import { setDefaultUnitValues } from './setDefaultUnitValues';
import { getDefaultPorts } from './setAddUnitPorts';
import type {
  AddStorageToScreenOutputsActionInterface,
  AddUnitActionInterface,
  AddUnitDataInterface,
  EditFlowPropertiesActionInterface,
  EditMultipleUnitLocationsActionInterface,
  EditPortsActionInterface,
  EditUnitFlowActionInterface,
  EditUnitInfoActionInterface,
  EditUnitLayoutActionInterface,
  EditUnitsLayoutActionInterface,
  HighlightNodesActionInterface,
  InsertOnConnectionActionInterface,
  RemoveUnitsActionInterface,
  ReplaceUnitInterface,
  ToggleMirroringActionInterface,
} from './sagaActions';
import {
  addUnit as addUnitAction,
  editLayout as editLayoutAction,
} from './sagaActions';
import {
  editGroup,
  removePanels,
  removeUnits as removeUnitsAction,
  setActiveUnit,
  setActiveUnitError,
  setAdditionalActiveUnitError,
  setChamberAnalysisResults,
  setConnections,
  setPorts,
  setSimulationResults,
  setUnits,
  updatePanels,
  updateUnits,
} from './reducer';
import type { SimulationResponseActionInterface } from 'store/simulation/actions';
import { simulationRequest } from 'store/simulation/actions';
import {
  getActiveUnitId,
  getChamberAnalysisResults,
  getPanels,
  getPorts,
  getSimulationResults,
  getUnits,
} from './selectors';
import { setDisplayDrawingSheet } from 'store/settings/reducer';
import {
  getAutoSimulate,
  getReverseSizeGradiation,
} from 'store/settings/selectors';
import { getUnitInfoData as getUnitInfoDataSelector } from 'store/unitInfoData/selectors';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import { getConnections } from 'store/connections/selectors';
import type { ConnectionInterface } from 'store/connections/types';
import { getVariant } from 'util/getVariant';
import { getUnitInfoData } from 'util/getUnitInfoData';
import { createConnectionId } from 'util/createConnectionId';
import { mergeUnit } from 'util/mergeUnit';
import { interposeNode } from '../../gojs/utils/interposeNode';
import { getPortsForUnit } from './unitRegistry';
import { getDefaultPanels, getPanelLoc } from 'util/getDefaultPanels';
import { getDefaultData as getGroupData } from 'unitData/HighlightGroup/getDefaultData';
import type { ChamberAnalysisResponseActionInterface } from 'store/chamberAnalysis/actions';
import { CHAMBER_ANALYSIS_RESULT } from 'store/chamberAnalysis/types';
import { mergeSelectableCrusherRiskFactorInclusionFlags } from 'util/riskAssessment';

const parseLoc = (loc: string) =>
  loc.split(' ').map((value) => parseInt(value, 10));

const offsetLoc = (loc: string, [deltaX, deltaY]: number[]) => {
  const [x, y] = parseLoc(loc);
  return `${x + deltaX} ${y + deltaY}`;
};

export function* editUnitInfo(action: EditUnitInfoActionInterface) {
  const { unitInfo, unitKey } = action.payload;
  try {
    const { units }: { units: UnitInterface[] } = yield select(
      (state: RootState) => state.unitConnGroup.present.unitState,
    );

    const unit = units.find((u) => u.key === unitKey);
    if (!unit) {
      return;
    }

    const updatedUnit = { ...unit, unitInfo };
    yield put(updateUnits([updatedUnit]));

    yield put(setActiveUnitError(false));
    yield put(setAdditionalActiveUnitError(false));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'editFlowProperties' },
    });
    console.error(e);
  }
}

/**
 * Edit inputFlow for a unit.
 * NOTE: This saga is specifically for units that have inputFlow in their unitInfo (e.g., FEED units).
 * The inputFlow property is stored in unitInfo.inputFlow, not at the root unit level.
 */
export function* editUnitFlow(action: EditUnitFlowActionInterface) {
  const { inputFlow, unitKey } = action.payload;
  try {
    const { units }: { units: UnitInterface[] } = yield select(
      (state: RootState) => state.unitConnGroup.present.unitState,
    );

    const unit = units.find((u) => u.key === unitKey);
    if (!unit) {
      return;
    }

    const updatedUnit = {
      ...unit,
      unitInfo: {
        ...unit.unitInfo,
        inputFlow,
      },
    };

    yield put(updateUnits([updatedUnit]));
    yield put(setActiveUnitError(false));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'editUnitFlow' },
      extra: { unitKey },
    });
    console.error(e);
  }
}

const getBaseUnit = (
  key: string | undefined,
  layout: GoJSInterface,
  unitInfoData: AddUnitDataInterface,
  reverseSizeGradiation: boolean,
  otherUnit?: UnitInterface,
) => {
  const id = typeof key === 'undefined' ? uuidv4() : key;

  const preDefinedSize = [...preDefinedFlowSizes[0].size];
  const preDefinedSizeReversed = reverseSizeGradiation
    ? preDefinedSize.reverse()
    : preDefinedSize;

  const baseUnit: UnitInterface = {
    key: id,
    type: layout.category as UnitTypes,
    layout: layout,
    unitInfo: {},
    flowProperties: {
      outputs: {},
      display: {
        displayPxxGradation: false,
        displayPxxValue1: true,
        displayPxxValue2: true,
        displayPxxValue3: true,
        displayGraph: false,
        displaySizeGradation: false,
        displaySize: true,
        displayAbrasionIndex: false,
        displayBulkDensity: false,
        displayClay: false,
        displayMoisture: false,
        displayNormalMassFlow: false,
        displayWorkIndex: false,
      },
      sizeMode: DisplayGraphMode.DISCHARGE_ONLY,
      graphMode:
        layout.category === UnitTypes.STORAGE
          ? DisplayGraphMode.FEED_AND_DISCHARGE
          : DisplayGraphMode.DISCHARGE_ONLY,
      pxxMode: DisplayGraphMode.DISCHARGE_ONLY,
      psdCalcMode: PSD_CALC_MODE.PERCENT,
      size: preDefinedSizeReversed,
      psdName: preDefinedFlowSizes[0].name,
      activePortIndex: 0,
    },
  };

  // merge default values with "otherUnit" values
  const unit = mergeUnit(
    setDefaultUnitValues(baseUnit, layout.category, unitInfoData),
    otherUnit,
  );

  if (otherUnit?.flowProperties) {
    unit.flowProperties = otherUnit.flowProperties;
  }

  const otherInputFlow =
    otherUnit?.unitInfo.inputFlow ??
    (otherUnit as unknown as { inputFlow?: unknown })?.inputFlow;
  if (
    otherInputFlow &&
    (unit.unitInfo.inputFlow !== undefined ||
      (unit as unknown as { inputFlow?: unknown }).inputFlow !== undefined)
  ) {
    unit.unitInfo = {
      ...unit.unitInfo,
      inputFlow: otherInputFlow as FlowInterface,
    };
  }

  return unit;
};

export function* addUnit(action: AddUnitActionInterface) {
  try {
    const { units, ports } = yield select(
      (state: RootState) => state.unitConnGroup.present.unitState,
    );
    const reverseSizeGradiation: boolean = yield select(
      getReverseSizeGradiation,
    );
    const allUnitInfoData: UnitInfoDataInterface = yield select(
      getUnitInfoDataSelector,
    );

    const {
      key,
      layout,
      unitInfoData,
      inputPortKeys,
      outputPortKeys,
      flowProperties,
      panels,
    } = action.payload;

    const unitSpecificData =
      unitInfoData ||
      getUnitInfoData(layout.category as UnitTypes, allUnitInfoData);
    const unit = getBaseUnit(
      key,
      layout,
      unitSpecificData,
      reverseSizeGradiation,
    );

    if (typeof action.payload.unitInfo !== 'undefined') {
      unit.unitInfo = action.payload.unitInfo;
    }

    if (flowProperties) {
      unit.flowProperties = { ...flowProperties };
    }

    if (action.payload.inputFlow) {
      unit.unitInfo = {
        ...unit.unitInfo,
        inputFlow: action.payload.inputFlow,
      };
    }

    const newUnits = [...units, unit];
    const unitPorts: PortInterface[] | undefined =
      typeof inputPortKeys !== 'undefined' &&
      typeof outputPortKeys !== 'undefined'
        ? getDefaultPorts(unit.key, layout, inputPortKeys, outputPortKeys)
        : typeof inputPortKeys !== 'undefined'
          ? getDefaultPorts(unit.key, layout, inputPortKeys)
          : typeof outputPortKeys !== 'undefined'
            ? getDefaultPorts(unit.key, layout, outputPortKeys)
            : getDefaultPorts(unit.key, layout);

    const newPorts = {
      ...ports,
      [unit.key]: unitPorts,
    };

    if (typeof newPorts !== 'undefined') {
      yield put(setPorts(newPorts));
    }
    yield put(updatePanels(getDefaultPanels(unit, panels)));

    yield put(setUnits(newUnits));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* addHighlight(action: HighlightNodesActionInterface) {
  const { unitKeys } = action.payload;
  const units: UnitInterface[] = yield select(getUnits);
  const selectedUnits = units.filter((u) => unitKeys.includes(u.key));

  if (action.payload.unitKeys.length === 0) {
    return;
  }

  const topLeft = selectedUnits.reduce(
    (acc: { x: number | null; y: number | null }, curr) => {
      const [x, y] = curr.layout.loc.split(' ').map((s) => parseInt(s, 10));
      if (acc.x === null || acc.y === null) {
        acc.x = x;
        acc.y = y;
      }
      if (x < acc.x) {
        acc.x = x;
      }
      if (y < acc.y) {
        acc.y = y;
      }
      return acc;
    },
    { x: null, y: null },
  );

  const textBox = {
    key: uuidv4(),
  };

  const group: Group = {
    ...getGroupData(),
    key: uuidv4(),
    textBoxKey: textBox.key,
    unitKeys: [...action.payload.unitKeys, textBox.key],
  };

  const layout: GoJSInterface = {
    category: UnitTypes.HIGHLIGHT_TEXT_BOX,
    hideNonMajorWarnings: false,
    mirrored: false,
    loc: `${(topLeft.x ?? 0) - 96} ${(topLeft.y ?? 0) - 120}`,
    inputPorts: 0,
    outputPorts: 0,
  };

  yield put(addUnitAction(layout, {}, textBox.key));
  yield put(editGroup({ key: group.key, group }));
  yield put(setActiveUnit(group.key));
}

export function* replaceUnit(action: ReplaceUnitInterface) {
  const { key, type, variantId } = action.payload;
  const units: UnitInterface[] = yield select(getUnits);
  const ports: PortsMapInterface = yield select(getPorts);
  const reverseSizeGradiation: boolean = yield select(getReverseSizeGradiation);
  const connections: ConnectionInterface[] = yield select(getConnections);
  const unitInfoData: UnitInfoDataInterface = yield select(
    getUnitInfoDataSelector,
  );

  const unit = units.find((u) => u.key === key);
  if (!unit) {
    return;
  }

  const unitInfo = getUnitInfoData(type, unitInfoData);

  const newUnit = getBaseUnit(
    key,
    {
      ...unit.layout,
      category: type,
      variantId: getVariant(type, variantId),
    },
    unitInfo,
    reverseSizeGradiation,
    unit,
  );

  const newUnits = units.reduce((acc: UnitInterface[], curr) => {
    if (curr.key === key) {
      return [...acc, newUnit];
    }
    return [...acc, curr];
  }, []);

  const newPorts = getDefaultPorts(unit.key, newUnit.layout);

  const nbrOfPorts = ports[unit.key].length;
  let newUnitPorts: PortInterface[] = [];
  const portDiff = nbrOfPorts - newPorts.length;

  let removedConnectionKeys: string[] = [];

  if (portDiff < 0) {
    // Ports are added
    newUnitPorts = [
      ...ports[unit.key],
      ...newPorts.slice(nbrOfPorts, newPorts.length),
    ];
  } else if (portDiff > 0) {
    // Ports and connections are removed
    newUnitPorts = ports[unit.key].slice(0, nbrOfPorts - portDiff);
    const removedPorts = ports[unit.key].slice(
      nbrOfPorts - portDiff,
      nbrOfPorts,
    );
    removedConnectionKeys = connections
      .filter((conn) => conn.to === key || conn.from === key)
      .flatMap((c) =>
        removedPorts.map((port) => createConnectionId(port.key, c.toPort)),
      );
  } else {
    // Same number of ports. Keep the old ports
    newUnitPorts = ports[unit.key];
  }

  const portsMap: PortsMapInterface = {
    ...ports,
    [unit.key]: newUnitPorts,
  };

  const newConnections = connections.filter(
    (connection) => !removedConnectionKeys.includes(connection.key),
  );

  yield put(setConnections(newConnections));
  yield put(setPorts(portsMap));
  yield put(setUnits(newUnits));
}

export function* removeUnits(action: RemoveUnitsActionInterface) {
  try {
    const keysToRemove = action.payload;
    const units: UnitInterface[] = yield select(getUnits);
    const activeUnit: string = yield select(getActiveUnitId);
    const ports: PortsMapInterface = yield select(getPorts);

    const unitsToRemove = units.filter((unit) =>
      keysToRemove.includes(unit.key),
    );

    const unitIsPrintWindow = unitsToRemove.find(
      (u) => u.type === UnitTypes.PRINT_WINDOW,
    );

    if (keysToRemove.includes(activeUnit)) {
      yield put(setActiveUnitError(false));
      yield put(setAdditionalActiveUnitError(false));
      yield put(setActiveUnit(null));
    }
    if (unitIsPrintWindow) {
      yield put(setDisplayDrawingSheet(false));
    }

    const modifiedPorts = Object.entries(ports).reduce((acc, [key, ports]) => {
      if (keysToRemove.includes(key)) {
        return acc;
      }
      return {
        ...acc,
        [key]: ports,
      };
    }, {});

    yield put(removeUnitsAction(keysToRemove));
    yield put(setPorts(modifiedPorts));
    yield put(removePanels(keysToRemove));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* editPort(action: EditPortsActionInterface) {
  try {
    const units: UnitInterface[] = yield select(getUnits);

    const { unitKey, diff, portMode } = action.payload;
    const unit = units.find((u) => u.key === unitKey);

    if (unit) {
      const { inputPorts, outputPorts } = unit.layout;
      yield call(
        editLayout,
        editLayoutAction(unitKey, {
          ...unit.layout,
          outputPorts:
            portMode === PortMode.OUTPUT ? outputPorts + diff : outputPorts,
          inputPorts:
            portMode === PortMode.INPUT ? inputPorts + diff : inputPorts,
        }),
      );
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(error);
  }
}

export function* insertOnConnection(action: InsertOnConnectionActionInterface) {
  const { connection, unitType, loc, variantId } = action.payload;
  const connections: ConnectionInterface[] = yield select(getConnections);
  const units: UnitInterface[] = yield select(getUnits);
  const ports: PortsMapInterface = yield select(getPorts);
  const unitInfoData: UnitInfoDataInterface = yield select(
    getUnitInfoDataSelector,
  );

  const fromUnit = units.find((u) => u.key === connection?.from);
  const toUnit = units.find((u) => u.key === connection?.to);

  if (!connection || !fromUnit || !toUnit) {
    return;
  }

  const layout: GoJSInterface = {
    category: unitType,
    loc,
    mirrored: false,
    hideNonMajorWarnings: false,
    ...getPortsForUnit(unitType, variantId),
    ...(variantId ? { variantId } : {}),
  };

  const unitInfo = getUnitInfoData(unitType, unitInfoData);
  const unit = getBaseUnit(undefined, layout, unitInfo, false);

  const { portsMap: insertedUnitPorts, connections: insertedUnitConnections } =
    interposeNode(unit, connection, fromUnit, toUnit);

  const newConnections = [
    ...connections.filter((c) => c.key !== connection.key),
    ...insertedUnitConnections,
  ];

  yield put(setPorts({ ...ports, ...insertedUnitPorts }));
  yield put(setUnits([...units, unit]));
  yield put(setConnections(newConnections));
  yield put(updatePanels(getDefaultPanels(unit)));
}

function* chamberyAnalysisResult(
  action: ChamberAnalysisResponseActionInterface,
) {
  try {
    const existingResults: {
      [unitKey: string]: ChamberAnalysisResultInterface;
    } = yield select(getChamberAnalysisResults);

    // Build chamber analysis results map
    const chamberAnalysisResultsMap = action.payload.units.reduce<
      Record<string, ChamberAnalysisResultInterface>
    >((acc, unitResponse) => {
      if (unitResponse.analysis) {
        const existing = existingResults[unitResponse.key];
        return {
          ...acc,
          [unitResponse.key]: {
            analysis: unitResponse.analysis,
            reference: existing?.reference,
            displaySettings: existing?.displaySettings ?? {
              showFeedSize: true,
              showCompressionZones: true,
              autoSimulate: true,
            },
          },
        };
      }
      return acc;
    }, {});

    yield put(setChamberAnalysisResults(chamberAnalysisResultsMap));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(error);
  }
}

/**
 * In order to update the ports output flow the simulation response ports are iterated.
 * Then the corresponding array is fetched with the help of the current port unitkey from the response.
 * From this array we can then fetch the port which will be updated, a new instance of this is created,
 * if the response port has an outflow and the current port exists.
 * Then the current index is fetched and a new instance of the port is created with the totalMass received fromt he simulation response port.
 * The previously fetched array is then updated with the new port and replaces the the one in the current state.
 */
export function* simulationResult(action: SimulationResponseActionInterface) {
  try {
    const ports: PortsMapInterface = yield select(getPorts);
    const currentSimulationResults: Record<string, SimulationResultInterface> =
      (yield select(getSimulationResults)) ?? {};

    const modifiedPorts = action.payload.ports.reduce(
      (acc: PortsMapInterface, curr) => {
        const unitPorts = acc[curr.unitKey];
        if (!unitPorts) {
          return acc;
        }
        const port = unitPorts.find((p) => p.key === curr.key);
        if (port) {
          const newPort: PortInterface = {
            ...port,
            outputFlow: curr.outputFlow,
          };

          const portArray = acc[port.unitKey].slice(); // Slice makes a copy of the array
          const currentIndex = portArray.indexOf(port);
          portArray.splice(currentIndex, 1, newPort); // Modifies the copy

          return {
            ...acc,
            [port.unitKey]: portArray,
          };
        }
        return acc;
      },
      ports,
    );

    // Build simulation results map from response
    const simulationResultsMap = action.payload.units.reduce<
      Record<string, SimulationResultInterface>
    >((acc, resUnit) => {
      return {
        ...acc,
        [resUnit.key]: {
          warnings: resUnit.warnings,
          simulationUnitInfo: resUnit.unitInfo,
          reportInfo: resUnit.reportInfo,
          riskAssessmentInfo: mergeSelectableCrusherRiskFactorInclusionFlags(
            resUnit.riskAssessmentInfo,
            currentSimulationResults[resUnit.key]?.riskAssessmentInfo,
          ),
        },
      };
    }, {});

    // Update ports
    yield put(setPorts(modifiedPorts));

    // Update simulation results in separate map
    yield put(setSimulationResults(simulationResultsMap));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* editFlowProperties(action: EditFlowPropertiesActionInterface) {
  try {
    const { unitKey, flowProperties } = action.payload;
    const units: UnitInterface[] = yield select(getUnits);
    const newUnits = units.reduce(
      (acc: UnitInterface[], curr) =>
        curr.key === unitKey ? [...acc, { ...curr, flowProperties }] : acc,
      [],
    );

    yield put(updateUnits(newUnits));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* editLayout(action: EditUnitLayoutActionInterface) {
  try {
    const { unitKey, layout } = action.payload;
    const units: UnitInterface[] = yield select(getUnits);

    const newUnits = units.reduce(
      (acc: UnitInterface[], curr) =>
        curr.key === unitKey ? [...acc, { ...curr, layout }] : acc,
      [],
    );

    yield put(updateUnits(newUnits));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* editLayouts(action: EditUnitsLayoutActionInterface) {
  try {
    const units: UnitInterface[] = yield select(getUnits);
    const edits = action.payload;

    const newUnits = units.reduce((acc: UnitInterface[], curr) => {
      const newLayout = edits.find((u) => u.unitKey === curr.key)?.layout;

      if (newLayout) {
        return [...acc, { ...curr, layout: newLayout }];
      }
      return [...acc, curr];
    }, []);

    yield put(updateUnits(newUnits));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(error);
  }
}

export function* toggleMirroring(action: ToggleMirroringActionInterface) {
  try {
    const { units } = yield select(
      (state: RootState) => state.unitConnGroup.present.unitState,
    );

    const modifiedUnits: UnitInterface[] = [...units];

    const editUnitLayoutIndex = modifiedUnits.findIndex(
      (u) => u.key === action.payload.unitKey,
    );
    if (editUnitLayoutIndex !== -1) {
      const unit: UnitInterface = modifiedUnits[editUnitLayoutIndex];
      const layout: GoJSInterface = modifiedUnits[editUnitLayoutIndex].layout;
      const newUnit = {
        ...unit,
        layout: {
          ...unit.layout,
          mirrored: !layout.mirrored,
        },
      };
      yield put(updateUnits([newUnit]));
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* editMultipleUnitLocations(
  action: EditMultipleUnitLocationsActionInterface,
) {
  try {
    const units: UnitInterface[] = yield select(getUnits);
    const panelMap: PanelMapInterface = yield select(getPanels);

    const { unitLocationsToEdit } = action.payload;
    const [updatedUnits, updatedPanelMap] = unitLocationsToEdit.reduce(
      (acc, curr) => {
        const unit = units.find((u) => u.key === curr.unitKey);

        if (!unit) {
          return acc;
        }

        const newUnit = {
          ...unit,
          layout: {
            ...unit.layout,
            loc: curr.location,
          },
        };
        acc[0].push(newUnit);

        const panels = panelMap[unit.key];
        const [oldX, oldY] = parseLoc(unit.layout.loc);
        const [newX, newY] = parseLoc(curr.location);
        const panelOffset = [newX - oldX, newY - oldY];

        const panelLoc = getPanelLoc(newUnit);

        acc[1] = {
          ...acc[1],
          [unit.key]: {
            ...panels,
            ...(panels?.[PanelType.INFO_PANEL]?.loc
              ? {
                  [PanelType.INFO_PANEL]: {
                    ...panels[PanelType.INFO_PANEL],
                    loc: offsetLoc(
                      panels[PanelType.INFO_PANEL].loc,
                      panelOffset,
                    ),
                  },
                }
              : {}),
            ...curr.panels.reduce((acc, type) => {
              return {
                ...acc,
                [type]: {
                  ...panels[type],
                  loc: panelLoc(type),
                },
              };
            }, {}),
          },
        };

        return acc;
      },
      [[], {}] as [Array<UnitInterface>, PanelMapInterface],
    );

    yield put(updatePanels(updatedPanelMap));
    yield put(updateUnits(updatedUnits));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'unitsSaga' },
    });
    console.error(e);
  }
}

export function* addStorageToScreenOutputsSaga(
  action: AddStorageToScreenOutputsActionInterface,
) {
  try {
    const { unitKey } = action.payload;
    const units: UnitInterface[] = yield select(getUnits);
    const ports: PortsMapInterface = yield select(getPorts);
    const connections: ConnectionInterface[] = yield select(getConnections);
    const reverseSizeGradiation: boolean = yield select(
      getReverseSizeGradiation,
    );
    const unitInfoData: UnitInfoDataInterface = yield select(
      getUnitInfoDataSelector,
    );

    const screenUnit = units.find((u) => u.key === unitKey);
    if (!screenUnit) return;

    const screenPorts = ports[unitKey] ?? [];
    const emptyOutputPorts = screenPorts.filter(
      (p) =>
        p.portMode === PortMode.OUTPUT &&
        p.portIndex <= screenUnit.layout.outputPorts &&
        !connections.some((c) => c.fromPort === p.key),
    );

    if (emptyOutputPorts.length === 0) return;

    const [screenX, screenY] = screenUnit.layout.loc
      .split(' ')
      .map((s) => parseInt(s, 10));

    const sortedOutputPorts = [...emptyOutputPorts].sort((a, b) =>
      screenUnit.layout.mirrored
        ? a.portIndex - b.portIndex
        : b.portIndex - a.portIndex,
    );

    const storageInfoData = getUnitInfoData(UnitTypes.STORAGE, unitInfoData);
    const newUnits: UnitInterface[] = [];
    const newPortsMap: PortsMapInterface = {};
    const newConnections: ConnectionInterface[] = [];

    sortedOutputPorts.forEach((outputPort) => {
      const storageKey = uuidv4();
      const inputPortKey = uuidv4();

      const portOffset = screenUnit.layout.mirrored
        ? outputPort.portIndex
        : screenUnit.layout.outputPorts - outputPort.portIndex;
      const storageX = screenX + portOffset * 96;
      const storageY = screenY + 192;

      const layout: GoJSInterface = {
        category: UnitTypes.STORAGE,
        variantId: 1,
        loc: `${storageX} ${storageY}`,
        mirrored: false,
        hideNonMajorWarnings: false,
        inputPorts: 1,
        outputPorts: 0,
      };

      const storageUnit = getBaseUnit(
        storageKey,
        layout,
        storageInfoData,
        reverseSizeGradiation,
      );

      const inputPort: PortInterface = {
        key: inputPortKey,
        unitKey: storageKey,
        portMode: PortMode.INPUT,
        portIndex: 0,
      };

      const connectionKey = createConnectionId(outputPort.key, inputPortKey);
      const connection: ConnectionInterface = {
        key: connectionKey,
        from: unitKey,
        to: storageKey,
        fromPort: outputPort.key,
        toPort: inputPortKey,
        conveyorCalc: false,
      };

      newUnits.push(storageUnit);
      newPortsMap[storageKey] = [inputPort];
      newConnections.push(connection);
    });

    yield put(setPorts({ ...ports, ...newPortsMap }));
    yield put(setUnits([...units, ...newUnits]));
    yield put(setConnections([...connections, ...newConnections]));
    for (const storageUnit of newUnits) {
      yield put(updatePanels(getDefaultPanels(storageUnit)));
    }

    const shouldAutoSimulate: boolean = yield select(getAutoSimulate);
    if (shouldAutoSimulate) {
      yield put(simulationRequest());
    }
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'addStorageToScreenOutputs' },
    });
    console.error(e);
  }
}

export function* unitSagas() {
  yield takeEvery(EDIT_UNIT_INFO, editUnitInfo);
  yield takeEvery(EDIT_UNIT_FLOW, editUnitFlow);
  yield takeEvery(ADD_UNIT, addUnit);
  yield takeEvery(REPLACE_UNIT, replaceUnit);
  yield takeLatest(EDIT_PORT, editPort);
  yield takeEvery(REMOVE_UNIT, removeUnits);
  yield takeLatest(INSERT_ON_CONNECTION, insertOnConnection);
  yield takeLatest(CHAMBER_ANALYSIS_RESULT, chamberyAnalysisResult);
  yield takeLatest(SIMULATION_RESULT, simulationResult);
  yield takeEvery(EDIT_FLOW_PROPERTIES, editFlowProperties);
  yield takeEvery(EDIT_LAYOUT, editLayout);
  yield takeEvery(EDIT_LAYOUTS, editLayouts);
  yield takeEvery(EDIT_MULTIPLE_UNIT_LOCATIONS, editMultipleUnitLocations);
  yield takeEvery(TOGGLE_MIRRORING, toggleMirroring);
  yield takeEvery(HIGHLIGHT_NODES, addHighlight);
  yield takeEvery(ADD_STORAGE_TO_SCREEN_OUTPUTS, addStorageToScreenOutputsSaga);
}
